import * as React from 'react';

import type { ViewStateProps } from './view-state';
import {
  deriveViewStateProps,
  type ViewStateIntegrationOptions,
  type ViewStateQuerySource,
} from './view-state-integration-core';

/**
 * Collapse a `@tyndo/hooks` query result into spread-ready `<ViewState>`
 * props — the loading/error/empty wiring every list view repeats, in one line.
 *
 * @example
 * const alarms = useApiQuery<Alarm[]>('/alarms/active');
 * const viewState = useViewStateIntegration(alarms, {
 *   errorFallbackMessage: 'Could not load active alarms.',
 * });
 * return (
 *   <ViewState {...viewState} emptyTitle="No active alarms">
 *     <ResponsiveTableView columns={columns} data={alarms.data ?? []} rowKey={(a) => a.id} />
 *   </ViewState>
 * );
 */
export function useViewStateIntegration<T>(
  query: ViewStateQuerySource<T>,
  options?: ViewStateIntegrationOptions<T>,
): Partial<ViewStateProps> {
  const { isEmpty, errorFallbackMessage, optimistic } = options ?? {};
  const { data, isPending, isLoading, isError, error, refetch } = query;
  return React.useMemo(
    () =>
      deriveViewStateProps(
        { data, isPending, isLoading, isError, error, refetch },
        { isEmpty, errorFallbackMessage, optimistic },
      ),
    [
      data,
      isPending,
      isLoading,
      isError,
      error,
      refetch,
      isEmpty,
      errorFallbackMessage,
      optimistic,
    ],
  );
}
